/**
 * Board 2 · First open: Promote has not prepared the protocol yet, or is preparing it. Three steps (find the
 * scenarios, sort the live tickets, write a reply for each) with where each stands.
 */

import { Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { BucketRun } from '@/types/websocket.types';
import type { PromotionJob } from '@/modules/promotion/types';
import type { LivePoolView } from '../types';
import { prepareStepsOf, type StepStatus } from './view-rules';

const StepMark = ({ status, index }: { status: StepStatus; index: number }) => (
	<span
		className={cn(
			'flex size-[22px] shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
			status === 'done' && 'bg-green-700 text-white',
			status === 'active' && 'border-2 border-gray-950 text-gray-950',
			status === 'waiting' && 'border border-gray-500 text-gray-800',
			status === 'failed' && 'border border-red-600 text-red-foreground'
		)}
		aria-hidden
	>
		{status === 'done' ? <Check className='size-3' strokeWidth={3} /> : index + 1}
	</span>
);

interface PrepareViewProps {
	view: LivePoolView;
	run: BucketRun | null;
	job: PromotionJob | null | undefined;
	canEdit: boolean;
	preparing: boolean;
	/** Set when the last prepare found no graded live ticket. */
	noLiveTickets: boolean;
	onPrepare: () => void;
}

export const PrepareView = ({ view, run, job, canEdit, preparing, noLiveTickets, onPrepare }: PrepareViewProps) => {
	const steps = prepareStepsOf(view, run, job);
	const working = preparing || steps.some((step) => step.status === 'active');
	const failed = view.typeGeneration?.status === 'failed' ? view.typeGeneration.error : null;
	return (
		<div className='flex min-h-0 flex-1 flex-col'>
			<div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5'>
				<div className='flex flex-col gap-1.5'>
					<p className='text-[15px] font-semibold text-text-heading'>Not promoted yet</p>
					<p className='text-[13px] leading-5 text-gray-800'>
						Promote reads the live tickets Kim answered on this protocol and gets every scenario ready, with a reply for
						each. Nothing changes for customers until you switch a scenario on.
					</p>
				</div>
				<ol className='flex flex-col rounded-[10px] border border-gray-500' aria-label='Preparing Promote'>
					{steps.map((step, index) => (
						<li
							key={step.title}
							className={cn(
								'flex gap-3 p-3.5',
								index < steps.length - 1 && 'border-b border-gray-300',
								step.status === 'active' && 'bg-gray-50'
							)}
						>
							<StepMark status={step.status} index={index} />
							<div className='flex min-w-0 flex-1 flex-col gap-1.5'>
								<span className='flex justify-between gap-2 text-[13px] font-medium text-text-heading'>
									{step.title}
									{step.count && <span className='text-[11px] font-normal text-gray-800 tabular-nums'>{step.count}</span>}
								</span>
								<span className='text-xs leading-[18px] text-gray-800'>{step.detail}</span>
								{step.progress !== undefined && (
									<div
										role='progressbar'
										aria-label={step.title}
										aria-valuemin={0}
										aria-valuemax={100}
										aria-valuenow={Math.round(step.progress * 100)}
										className='h-1.5 overflow-hidden rounded-full bg-gray-300'
									>
										<div className='h-full bg-gray-950' style={{ width: `${Math.round(step.progress * 100)}%` }} />
									</div>
								)}
							</div>
						</li>
					))}
				</ol>
				{noLiveTickets && (
					<p className='rounded-lg bg-gray-50 px-3 py-2.5 text-xs leading-[18px] text-gray-900' role='status'>
						No graded live tickets on this protocol in the last 30 days, so nothing was written. Promote again once Kim
						has answered some in Sentinel.
					</p>
				)}
				{failed && (
					<p className='rounded-lg bg-red-bg px-3 py-2.5 text-xs leading-[18px] text-red-foreground' role='alert'>
						{failed}
					</p>
				)}
			</div>
			<div className='flex shrink-0 items-center justify-between gap-3 border-t border-gray-500 px-5 py-3'>
				<span className='text-xs text-gray-800'>Takes a few minutes. You can close this panel.</span>
				<Button size='sm' onClick={onPrepare} disabled={!canEdit || working}>
					{working && <Loader2 className='animate-spin' aria-hidden />}
					{working ? 'Preparing…' : failed ? 'Try again' : 'Prepare'}
				</Button>
			</div>
		</div>
	);
};
